// @flow
import React from "react";

import { BuilderSelectionPicker } from "bb-selection-picker";
import { BuilderTabSelector } from "bb-tab-selector";
import { BuilderConductor } from "bb-conductor";
import { BuilderBetslip } from "bb-betslip";

import type { PriceFormat, ApiConfig } from "bb-api-sdk";

import * as Stores from "./stores";
import { SoccerTabItems } from "./model/soccer";
import { BasketballTabItems } from "./model/basketball";
import { RugbyTabItems } from "./model/rugby";
import { NFLTabItems } from "./model/nfl";
import globalConfig from "./config";

import loadStyles from "./styleLoader";

/**
 * import SG themes for building
 */
import "./themes/sg/main.scss";

/**
 * import BCLC themes for building
 */
import "./themes/bclc/main.scss";

/**
 * import NLO themes for building
 */
import "./themes/nlo/main.scss";

/**
 * import NZRB themes for building
 */
import "./themes/nzrb/main.scss";

/**
 * import Danske Spil themes for building
 */
import "./themes/ds/main.scss";

/**
 * import Caesars themes for building
 */
import "./themes/czr/main.scss";

/**
 * import Golden nugget themes for building
 */
import "./themes/gn/main.scss";

/**
 * import BetFred themes for building
 */
import "./themes/bfc/main.scss";

/**
 * import Wind Creek themes for building
 */
import "./themes/wc/main.scss";

/**
 * import Hard Rock themes for building
 */
import "./themes/hrc/main.scss";


/**
 * import the customisable Api Services
 */
import SGPricingService from "./apis/pricing";
import SGContentService from "./apis/content";

/**
 * Instantiate the Pricing API service clients
 *
 * @ignore
 * @type {SGPricingService}
 */
const pricingService = new SGPricingService();

/**
 * Instantiate the Content API service clients
 *
 * @ignore
 * @type {SGContentService}
 */
const contentService = new SGContentService();
/**
 * Global configuration to apply to BB-widgets
 *
 * @type {null}
 */

export type BBConfig = {
  /**
   * Operator theme to apply
   */
  style: "SG" | "CZR" | "BCLC" | "DS" | "GN" | "NLO" | "NZRB" | "BFC" | "WC" | "HRC",
  /**
   * Price format in betslip
   */
  priceFormat: PriceFormat,
  /**
   * Translations
   */
  locale: "en" | "it" | "nl-NL" | "da-DK",
  /**
   * Configuration to apply to the Content API
   */
  contentApiConfig: ApiConfig,
  /**
   * Configuration to apply to the Pricing API
   */
  betServiceApiConfig: ApiConfig,
  /**
   * Maximum number of selections to display by default in selection picker before tapping 'Show More'
   */
  maxSelectionsPerMarket: number,
};


/**
 * At this time BetBuilder only work for soccer so we hardcode it in this case
 */
export type BuilderTabSelectorSportType =
  | "football"
  | "soccer"
  | "basketball"
  | "rugby_union"
  | "rugby_league"
  | "american_footbal";

/**
 * Stores the global config object
 *
 * @type {null}
 */
// let globalConfig: BBConfig = null;

/**
 * Returns the list of tab items for a particular sport
 *
 * @param tabType
 * @returns {null|*[]}
 */
const tabItems = (tabType: BuilderTabSelectorSportType) => {  
  switch (tabType.toLowerCase()) {
    case "football":
    case "soccer":
      return SoccerTabItems;
    case "basketball":
      return BasketballTabItems;
    case "rugby_union":
    case "rugby_league":
      return RugbyTabItems;
    case "american_footbal":
      return NFLTabItems;
    default:
      throw Error(
        "TabBar encountered an invalid sport type '" +
          tabType +
          "'. Valid types are: 'football, basketball, rugby_union, rugby_league & american_footbal'"
      );
  }
};

const localeVaildation = (locale: string) => {
  switch(locale.toLowerCase()) {
    case "en":
    case "en-us":
      return "en-GB";
    default: 
      return locale;
  }
}

/**
 * The widget suite
 *
 * @type {{
 *  betslip: {new: (function(): {render: (function(*): *)})},
 *  selectionPicker: {new: (function(): {render: (function(*): *)})},
 *  tabBar: {new: (function(*): {render: (function(*): *)})},
 *  configure: widgets.configure,
 *  conductor: {new: (function(): {conduct: (function(): *)})}
 * }}
 */
export const widgets = {
  /**
   * Configure the Widget suite
   *
   * @param config
   */
  configure: (config: BBConfig) => {
    if (config.style == null) throw Error("Missing operator type in config");
    const style = config.style;
    loadStyles(style);
    globalConfig.productName = config.productName || "BLDR";
    globalConfig.style = config.style;
    globalConfig.priceFormat = config.priceFormat;
    globalConfig.locale = localeVaildation(config.locale);
    globalConfig.contentApiConfig = config.contentApiConfig;
    globalConfig.betServiceApiConfig = config.betServiceApiConfig;
    globalConfig.maxSelectionsPerMarket = config.maxSelectionsPerMarket;
  },
  /**
   * Conductor
   */
  conductor: {
    /**
     * Instantiate a new conductor
     *
     * @returns {{conduct: (function(): *)}}
     */
    new: () => {
      let store = Stores.conductorStore();
      return {
        /**
         * Start conducting the widgets
         *
         * @returns {*}
         */
        conduct: () => {
          return (
            <BuilderConductor
              store={store}
              onMount={() => {
                store.broadcast.receiver().startListening();
              }}
              onDismount={() => {
                store.broadcast.receiver().stopListening();
              }}
            />
          );
        },
      };
    },
  },
  /**
   * Betslip
   */
  betslip: {
    /**
     * Instantiate a new Betslip
     * @returns {{render: (function(*): *)}}
     */
    new: () => {
      let store = Stores.betslipStore();
      return {
        /**
         * Render the betslip component
         *
         * @param args
         * @returns {*}
         */
        render: (args) => {
          return (
            <BuilderBetslip
              store={store}
              apiConfig={globalConfig.betServiceApiConfig}
              pricingService={pricingService}
              contentService={contentService}
              eventId={args.eventId}
              priceFormat={args.priceFormat || globalConfig.priceFormat}
              locale={globalConfig.locale}
              productName={globalConfig.productName}
              onMount={() => {
                store.broadcast.receiver().startListening();
              }}
              onDismount={() => {
                store.broadcast.receiver().stopListening();
              }}
            />
          );
        },
      };
    },
  },
  /**
   * Selection picker
   */
  selectionPicker: {
    /**
     * Instantiate a new selection picker
     *
     * @returns {{render: (function(*): *)}}
     */
    new: () => {
      let store = Stores.pickerStore();
      return {
        /**
         * Render the Selection Picker
         *
         * @param args
         * @returns {*}
         */
        render: (args) => {
          return (
            <BuilderSelectionPicker
              store={store}
              apiConfig={globalConfig.contentApiConfig}
              contentService={contentService}
              eventId={args.eventId}
              priceFormat={globalConfig.priceFormat}
              locale={globalConfig.locale}
              maxSelectionsPerMarket={globalConfig.maxSelectionsPerMarket}
              productName={globalConfig.productName}
              onMount={() => {
                store.broadcast.receiver().startListening();
              }}
              onDismount={() => {
                store.broadcast.receiver().stopListening();
              }}
            />
          );
        },
      };
    },
  },
  /**
   * TabBar
   */
  tabBar: {
    /**
     * Instantiate a new TabBar
     *
     * @param config
     * @returns {{render: (function(*): *)}}
     */
    new: (config) => {
      let store = Stores.tabStore();
      return {
        /**
         * Render the TabBar component
         *
         * @param args
         * @returns {*}
         */
        render: (args) => {
          return (
            <BuilderTabSelector
              store={store}
              tabItems={tabItems(config.sportType)}
              locale={globalConfig.locale}
              onMount={() => {
                store.broadcast.receiver().startListening();
              }}
              onDismount={() => {
                store.broadcast.receiver().stopListening();
              }}
            />
          );
        },
      };
    },
  },
};



// WEBPACK FOOTER //
// ./src/index.js